import { DeleteOutlined, EditOutlined, ExclamationCircleFilled, ExpandOutlined } from "@ant-design/icons";
import { Button, Col, Row, Tooltip } from "antd";
import classNames from "classnames";
import { FormFieldEditorFields } from "components/Forms/FormFieldEditorFields/FormFieldEditorFields";
import { useFormFieldOverrides } from "components/Forms/FormFieldsForm/useFormFieldOverrides";
import { DrawerLink } from "components/UI/DrawerLink";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC } from "react";
import { useWatch } from "react-hook-form";
import { FormFieldOverride } from "types/enums";
import { DrawerId } from "types/models.ts";

import { isSavedFormField } from "./utils/isSavedFormField";

import FormFieldData = App.Data.FormFieldData;

interface FormFieldsEditorItemProps {
    onDelete: () => void;
    index: number;
    fieldKey: string;
    formStructureId: string;
    deletedIds: Array<string>;
    setDeletedIds: (ids: Array<string> | ((prevIds: Array<string>) => Array<string>)) => void;
}

export const FormFieldsEditorItem: FC<FormFieldsEditorItemProps> = ({
    onDelete,
    index,
    fieldKey,
    formStructureId,
    deletedIds,
    setDeletedIds,
}) => {
    const { t } = usePaldockTranslation("forms.form-fields");
    const generalFormUtils = useGeneralForm();
    const { control } = generalFormUtils;

    const type = useWatch({
        control,
        name: `formFields.${index}.type`,
    });

    const formField = useWatch({
        control,
        name: `formFields.${index}`,
    }) as FormFieldData;

    const isDeleted = formField?.id && deletedIds.includes(formField.id);
    const isSaved = Boolean(formField?.id && isSavedFormField(formField.id));
    const isSourceLinked = Boolean(formField?.global_form_field_id || formField?.parent_form_field_id);
    const propertyPath = `formFields.${index}`;
    const { isOverrideAvailable, isOverridden, toggleOverride } = useFormFieldOverrides(
        propertyPath,
        formField?.overrideContext
    );

    const overrideControl = (override: FormFieldOverride) => ({
        available: isOverrideAvailable(override),
        overridden: isOverridden(override),
        onToggle: () => toggleOverride(override),
    });

    const systemNameSuffix = (
        <div className="flex items-center gap-1">
            <DrawerLink
                drawerId={DrawerId.ParentField}
                search={{ [`${DrawerId.ParentField}-fieldKey`]: fieldKey }}
                action="show"
            >
                <Button
                    type="text"
                    size="small"
                    icon={<ExpandOutlined className="text-[#00000073]" />}
                    aria-label={t("source.select")}
                />
            </DrawerLink>
            {isSourceLinked && (
                <Tooltip title={t("source.linked")}>
                    <ExclamationCircleFilled className="text-[#FAAD14]" />
                </Tooltip>
            )}
        </div>
    );

    return (
        <Row gutter={10} align="middle" className={classNames(isDeleted && "opacity-50")}>
            <FormFieldEditorFields
                propertyPath={propertyPath}
                systemNameDisabled={isSourceLinked}
                sourceLinked={isSourceLinked}
                systemNameSuffix={systemNameSuffix}
                visibleDescriptionOverride={overrideControl(FormFieldOverride.VisibleDescription)}
                placeholderOverride={overrideControl(FormFieldOverride.Placeholder)}
            />
            <Col span={2}>
                <div className="flex w-full items-center justify-end gap-2">
                    {formField?.id ? (
                        <DrawerLink
                            drawerId={DrawerId.FieldSettings}
                            search={{
                                [`${DrawerId.FieldSettings}-formFieldId`]: formField?.id,
                                [`${DrawerId.FieldSettings}-formStructureId`]: formStructureId,
                                [`${DrawerId.FieldSettings}-type`]: type,
                            }}
                            action="show"
                            disabled={!type || !isSaved}
                        >
                            <Button
                                type="text"
                                className="!bg-transparent !shadow-none"
                                disabled={!type || !isSaved}
                                icon={<EditOutlined className="text-[#000000E0]" />}
                            />
                        </DrawerLink>
                    ) : (
                        <Button
                            type="text"
                            className="!bg-transparent !shadow-none"
                            disabled
                            icon={<EditOutlined className="text-[#000000E0]" />}
                        />
                    )}
                    <Button
                        type="text"
                        className="!bg-transparent !shadow-none"
                        icon={<DeleteOutlined className="text-[#000000E0]" />}
                        onClick={() => {
                            if (formField?.id) {
                                const formFieldId = formField.id;
                                setDeletedIds((prevState) => [...prevState, formFieldId]);
                            } else {
                                onDelete();
                            }
                        }}
                    />
                </div>
            </Col>
        </Row>
    );
};
